import React, { Component, ReactNode } from 'react';
import {
	DeviceType,
	Orientation,
	SofaRangeName,
	WriteDirection,
	YmalSectionType,
	YmalType,
} from 'sofa-reborn-react';
import ProductShelfProductCardMediaEnum from '@inter-ikea-kompis/component-product-shelf/lib/enums/ProductShelfProductCardMediaEnum';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { IDexfSettings, IProduct, ITranslations } from '@inter-ikea-kompis/types';
import { StyledAdditionalCover, StyledYmal, StyledYmalItems, YmalHeader } from './YmalViewStyle';
import { localeService } from '../../services';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import Card from '@ingka/card';
import { ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities';
import { SummaryPageTabEnum } from '@inter-ikea-kompis/component-summary-page';
import {
	KompisText,
	KompisProductShelf,
	KompisFocusOutline,
} from '@inter-ikea-kompis/react-components';
import { IProductShelfVisibleModal } from '@inter-ikea-kompis/component-product-shelf';

type Props = {
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	selectedIndex: number;
	selectedTab: SummaryPageTabEnum;
	ymalProducts: Array<IProduct>;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	visibleModal: IProductShelfVisibleModal | null;
	sofaRangeName: SofaRangeName;
	showAdditionalCover: boolean;
	showYmalContainer: boolean;
	onYmalAddToCart: (
		e: CustomEvent,
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	) => void;
	onYmalAddToList: (
		e: CustomEvent,
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	) => void;
	onYmalPipLinkClick: (e: CustomEvent) => void;
	onChange: (e: CustomEvent) => void;
	onModalOpen: (e: CustomEvent) => void;
	onModalClose: () => void;
	onAdditionalCoverClick: () => void;
};

/**
 * React component.
 *
 */
export default class YmalView extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;

		if (!props.kompisSettings || !props.kompisTranslations) {
			return null;
		}

		return (
			<StyledYmal devicetype={props.deviceType} showYmalContainer={props.showYmalContainer}>
				<YmalHeader>
					<KompisText type={ThemeFontStyleTypeEnum.headingMedium} theme={SkapaTheme}>
						{(props.showYmalContainer || props.showAdditionalCover) &&
						props.selectedTab !== SummaryPageTabEnum.productList
							? localeService
									.getTranslation('summaryRelatedProductsHeader')
									.replace('[[applicationname]]', props.sofaRangeName)
							: localeService.getTranslation('ymalHeading')}
					</KompisText>
				</YmalHeader>
				<StyledYmalItems deviceType={props.deviceType} orientation={props.orientation}>
					{props.showAdditionalCover && !props.showYmalContainer && (
						<StyledAdditionalCover
							deviceType={props.deviceType}
							orientation={props.orientation}
							writeDirection={props.writeDirection}
						>
							<KompisFocusOutline
								// @ts-expect-error Missmatched kompis type
								onKeyDown={(e: KeyboardEvent): void => {
									if (e.key === 'Enter' || e.key === ' ') {
										props.onAdditionalCoverClick && props.onAdditionalCoverClick();
									}
								}}
								theme={SkapaTheme}
								tabIndex={0}
								style={{ width: 'inherit' }}
							>
								<Card
									backgroundColor="#CDDED6"
									buttonVariant="icon-primary-inverse"
									body={localeService.getTranslation('additionalCoverCardText')}
									compact
									title={localeService
										.getTranslation('additionalCoverCardHeader')
										.replace('[[applicationname]]', props.sofaRangeName)}
									emphasised
									href="#"
									imagePosition="trailing"
									imageProps={{
										src: 'https://www.ikea.com/se/sv/images/products/soederhamn-3-sitssoffa-tallmyra-ljusgroen__0780880_pe760275_s5.jpg?f=xl',
									}}
									onClick={props.onAdditionalCoverClick}
									role="button"
									tabIndex={-1}
								/>
							</KompisFocusOutline>
						</StyledAdditionalCover>
					)}
					{props.kompisSettings && props.kompisTranslations && (
						<KompisProductShelf
							onAddToCart={(e: CustomEvent) =>
								props.onYmalAddToCart(e, {
									ymalType: 'Cart',
									ymalSectionType: 'general',
								})
							}
							onAddToList={(e: CustomEvent) =>
								props.onYmalAddToList(e, {
									ymalType: 'List',
									ymalSectionType: 'general',
								})
							}
							onLinkClick={(e: CustomEvent) => props.onYmalPipLinkClick(e)}
							onModalOpen={props.onModalOpen}
							onModalClose={props.onModalClose}
							onChange={props.onChange}
							theme={SkapaTheme}
							selectedIndex={props.selectedIndex}
							settings={props.kompisSettings}
							translations={props.kompisTranslations.kompis}
							/* List of items with a object format corresponding to { IProduct } from '@inter-ikea-kompis/types' */
							products={props.ymalProducts}
							visibleModal={props.visibleModal}
							productCardMedia={ProductShelfProductCardMediaEnum.switchImageOnHover}
							skipListingLabel={localeService.getTranslation('skipProductList')}
							productMeasures={ProductDescriptionProductMeasureEnum.short}
						/>
					)}
				</StyledYmalItems>
			</StyledYmal>
		);
	}
}
